import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import "./WallboxOverlayPage.scss";

type TargetType = "driver" | "host" | "combo";
type Orientation = "default" | "portrait" | "landscape";

const OVERLAY_SRCS: Record<TargetType, Record<Orientation, string>> = {
  combo: {
    default: `${process.env.PUBLIC_URL}/images/frames/common/wallbox-frame-overlay.png`,
    portrait: `${process.env.PUBLIC_URL}/images/frames/common/wallbox-frame-overlay-portrait.png`,
    landscape: `${process.env.PUBLIC_URL}/images/frames/common/wallbox-frame-overlay-landscape.png`,
  },
  driver: {
    default: `${process.env.PUBLIC_URL}/images/frames/driver/wallbox-frame-overlay-drivers.png`,
    portrait: `${process.env.PUBLIC_URL}/images/frames/driver/wallbox-frame-overlay-portrait-drivers.png`,
    landscape: `${process.env.PUBLIC_URL}/images/frames/driver/wallbox-frame-overlay-landscape-drivers.png`,
  },
  host: {
    default: `${process.env.PUBLIC_URL}/images/frames/host/wallbox-frame-overlay-hosts.png`,
    portrait: `${process.env.PUBLIC_URL}/images/frames/host/wallbox-frame-overlay-portrait-hosts.png`,
    landscape: `${process.env.PUBLIC_URL}/images/frames/host/wallbox-frame-overlay-landscape-hosts.png`,
  },
};

const TARGET_OPTIONS = [
  { label: "Driver", value: "driver" },
  { label: "Host", value: "host" },
  { label: "Combo", value: "combo" },
];

const ORIENTATION_OPTIONS = [
  { label: "Default", value: "default" },
  { label: "Portrait", value: "portrait" },
  { label: "Landscape", value: "landscape" },
];

interface PhotoPosition {
  x: number;
  y: number;
  scale: number;
}

const WallboxOverlayPage: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayImgRef = useRef<HTMLImageElement | null>(null);
  const photoImgRef = useRef<HTMLImageElement | null>(null);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    photoX: number;
    photoY: number;
  } | null>(null);

  const [targetType, setTargetType] = useState<TargetType>("combo");
  const [orientation, setOrientation] = useState<Orientation>("default");
  const [overlayReady, setOverlayReady] = useState(false);
  const [overlayError, setOverlayError] = useState(false);
  const [photoReady, setPhotoReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pos, setPos] = useState<PhotoPosition>({ x: 0, y: 0, scale: 1 });

  // Reload overlay when target type or orientation changes
  useEffect(() => {
    const src = OVERLAY_SRCS[targetType][orientation];
    setOverlayReady(false);
    setOverlayError(false);
    const img = new Image();
    img.onload = () => {
      overlayImgRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      // Re-center photo for new overlay dimensions
      const photo = photoImgRef.current;
      if (photo) {
        const scale = Math.max(
          img.naturalWidth / photo.naturalWidth,
          img.naturalHeight / photo.naturalHeight
        );
        setPos({
          x: (img.naturalWidth - photo.naturalWidth * scale) / 2,
          y: (img.naturalHeight - photo.naturalHeight * scale) / 2,
          scale,
        });
      }
      setOverlayReady(true);
    };
    img.onerror = () => setOverlayError(true);
    img.src = src;
  }, [targetType, orientation]);

  // Non-passive wheel handler so we can call preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.05 : 0.95;
      setPos((prev) => ({
        ...prev,
        scale: Math.max(0.05, prev.scale * factor),
      }));
    };
    canvas.addEventListener("wheel", handler, { passive: false });
    return () => canvas.removeEventListener("wheel", handler);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const overlay = overlayImgRef.current;
    if (!canvas || !overlay || !overlayReady) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const photo = photoImgRef.current;
    if (photo && photoReady) {
      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.scale(pos.scale, pos.scale);
      ctx.drawImage(photo, 0, 0);
      ctx.restore();
    }

    ctx.drawImage(overlay, 0, 0);
  }, [overlayReady, photoReady, pos]);

  useEffect(() => {
    draw();
  }, [draw]);

  const loadPhoto = (file: File) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      photoImgRef.current = img;
      const overlay = overlayImgRef.current;
      if (overlay) {
        const scale = Math.max(
          overlay.naturalWidth / img.naturalWidth,
          overlay.naturalHeight / img.naturalHeight
        );
        setPos({
          x: (overlay.naturalWidth - img.naturalWidth * scale) / 2,
          y: (overlay.naturalHeight - img.naturalHeight * scale) / 2,
          scale,
        });
      }
      setPhotoReady(true);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const openFilePicker = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) loadPhoto(file);
    };
    input.click();
  };

  const resetPosition = () => {
    const overlay = overlayImgRef.current;
    const photo = photoImgRef.current;
    if (!overlay || !photo) return;
    const scale = Math.max(
      overlay.naturalWidth / photo.naturalWidth,
      overlay.naturalHeight / photo.naturalHeight
    );
    setPos({
      x: (overlay.naturalWidth - photo.naturalWidth * scale) / 2,
      y: (overlay.naturalHeight - photo.naturalHeight * scale) / 2,
      scale,
    });
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const name = window.prompt("Save as:", "wallbox-photo-overlay");
    if (name === null) return; // cancelled
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.trim() || "wallbox-photo-overlay"}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!photoReady) return;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      photoX: pos.x,
      photoY: pos.y,
    };
    setIsDragging(true);
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const d = dragStartRef.current;
    if (!d) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratioX = canvas.width / rect.width;
    const ratioY = canvas.height / rect.height;
    setPos((prev) => ({
      ...prev,
      x: d.photoX + (e.clientX - d.mouseX) * ratioX,
      y: d.photoY + (e.clientY - d.mouseY) * ratioY,
    }));
  };

  const stopDrag = () => {
    dragStartRef.current = null;
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) loadPhoto(file);
  };

  return (
    <div className="wallbox-overlay-page surface-section p-4">
      <div className="mb-4">
        <h2 className="m-0">Wallbox Photo Overlay</h2>
        <p className="text-color-secondary mt-1 mb-0">
          Upload a station photo, position it inside the cocharge frame, and
          download the result.
        </p>
      </div>

      {overlayError && (
        <Message
          severity="warn"
          className="mb-4 block"
          text={`Overlay image not found: ${OVERLAY_SRCS[targetType][orientation]}`}
        />
      )}

      <Card>
        <div className="flex flex-column gap-8 mb-4">
          <div className="flex align-items-center gap-3 flex-wrap">
            <Button
              label="Upload Photo"
              icon="pi pi-upload"
              onClick={openFilePicker}
            />
            {photoReady && (
              <>
                <span className="mx-3" />
                <Button
                  label="Reset Position"
                  icon="pi pi-refresh"
                  outlined
                  onClick={resetPosition}
                />
                <Button
                  label="Download Image"
                  icon="pi pi-download"
                  outlined
                  onClick={handleDownload}
                />
              </>
            )}
          </div>
          <div className="flex align-items-center gap-3 flex-wrap">
            <SelectButton
              value={targetType}
              options={TARGET_OPTIONS}
              onChange={(e) => e.value && setTargetType(e.value as TargetType)}
            />
            <SelectButton
              value={orientation}
              options={ORIENTATION_OPTIONS}
              onChange={(e) => e.value && setOrientation(e.value)}
            />
          </div>
        </div>
        {photoReady && (
          <p className="text-sm text-color-secondary mt-0 mb-3">
            Drag to reposition · Scroll to zoom
          </p>
        )}

        <div
          className={`canvas-wrapper${isDragOver ? " drag-over" : ""}`}
          onDrop={onDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
        >
          {!overlayReady && !overlayError && (
            <div className="canvas-placeholder">
              <i className="pi pi-spin pi-spinner text-4xl text-color-secondary" />
            </div>
          )}
          <canvas
            ref={canvasRef}
            style={{
              cursor: isDragging ? "grabbing" : photoReady ? "grab" : "default",
              display: overlayReady ? "block" : "none",
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          />
          {overlayReady && !photoReady && (
            <div className="canvas-drop-overlay">
              <i className="pi pi-upload text-3xl mb-2" />
              <span>Drop a station photo here</span>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default WallboxOverlayPage;
